import "./styles.css";
import "./era-themes.css";
import { useState, useEffect, useMemo } from "react";
import useUrlParams from "./hooks/useUrlParams";
import Poll from "./Poll";

import { motion, AnimatePresence } from "framer-motion";
import GlobalStatsModal from "./GlobalStatsModal";
import PatchNotesModal, { PATCHES } from "./PatchNotesModal";
import RoadmapModal from "./RoadmapModal";
import { Analytics } from "@vercel/analytics/react";
import {
  CHARACTERS_MAP,
  MUSIC_MAP,
  CRAFTS_MAP,
  QUOTES_MAP,
  TRIVIA_MAP,
  LOCATIONS_MAP,
  SILHOUETTES_MAP,
} from "./maps";
import { BACKGROUNDS, CHARACTERS, QUOTES, MUSIC, LOCATIONS, TRIVIA, CRAFTS, SILHOUETTES } from "./data";

import CountdownTimer from "./components/CountdownTimer";
import CharacterMode from "./components/CharacterMode";
import QuoteMode from "./components/QuoteMode";
import MusicMode from "./components/MusicMode";
import LocationMode from "./components/LocationMode";
import TriviaMode from "./components/TriviaMode";
import CraftsMode from "./components/CraftsMode";
import SilhouetteMode from "./components/SilhouetteMode";
import ResultsMode from "./components/ResultsMode";
import ProfileMode from "./components/ProfileMode";
import CollectionMode from "./components/CollectionMode";
import FactionMode from "./components/FactionMode";
import FactionBadge from "./components/FactionBadge";
import ArchiveMode from "./components/ArchiveMode";
import SettingsModal from "./components/SettingsModal";
import LeaderboardModal from "./LeaderboardModal";
import ModeBadge from "./components/ModeBadge";
import Banner from "./components/Banner";
import { getDateString, getDailyItem } from "./utils/gameLogic";
import { useDailyLocalStorage, usePersistentStorage } from "./hooks/useGameHooks";
import MigrationModal from "./components/MigrationModal";
import MapCoordLogger from "./components/MapCoordLogger";

/**
 * Root application component — orchestrates routing between 7 game modes,
 * results, profile, collection, and archive views. Manages date offset (admin),
 * era theme detection/cycling, daily background rotation, sidebar navigation,
 * and all modal dialogs (settings, global stats, patch notes, roadmap, migration).
 *
 * @returns {JSX.Element} The full Kisekidle app shell.
 */
export default function App() {
  const { activeTab, setActiveTab, targetDateObj, setTargetDateObj } = useUrlParams();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const appVersion = "new";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [adminOffset, setAdminOffset] = useState(0);
  const baseDate = targetDateObj ? new Date(targetDateObj) : new Date();
  if (adminOffset !== 0) {
    baseDate.setUTCDate(baseDate.getUTCDate() + adminOffset);
  }

  const activeDateObj = baseDate;
  const activeDateStr = getDateString(activeDateObj);
  const isArchive = targetDateObj !== null;

  const [userTheme, setUserTheme] = usePersistentStorage("kisekidle-theme", "legacy");
  const [scanlinesEnabled, setScanlinesEnabled] = usePersistentStorage("kisekidle-scanlines", true);
  const [staticBgEnabled, setStaticBgEnabled] = usePersistentStorage("kisekidle-static-bg", false);
  const [collectionExpandDefault, setCollectionExpandDefault] = usePersistentStorage("kisekidle-collection-expand", false);
  const [showMigrationBanner, setShowMigrationBanner] = usePersistentStorage("kisekidle-show-migration-banner", true);
  const [showGlobalBanner, setShowGlobalBanner] = usePersistentStorage("kisekidle-show-global-banner", false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);

  // Generate anonymous player ID on first visit
  useEffect(() => {
    if (!localStorage.getItem("kisekidle-player-id")) {
      localStorage.setItem("kisekidle-player-id", crypto.randomUUID());
    }
  }, []);

  const isOldDomain = window.location.hostname === "kisekidle.vercel.app";

  // Daily Background Rotation Logic
  const epochDate = new Date("2026-04-01T00:00:00Z");
  const targetUTC = Date.UTC(
    activeDateObj.getUTCFullYear(),
    activeDateObj.getUTCMonth(),
    activeDateObj.getUTCDate()
  );
  const diffDays = Math.floor((targetUTC - epochDate.getTime()) / (1000 * 60 * 60 * 24));
  const dailyBg = BACKGROUNDS[Math.max(0, diffDays) % BACKGROUNDS.length];

  /**
   * Resolves the active era theme: manual choice, random (mode-based deterministic
   * rotation), or falls back to "legacy".
   */
  const currentTheme = useMemo(() => {
    let theme = userTheme;

    if (userTheme === "random") {
      const themes = ["legacy", "sky", "coldsteel", "daybreak"];
      const modeNames = ["characters", "quotes", "music", "locations", "trivia", "crafts", "silhouette", "results", "profile", "collection", "archive"];
      const modeIndex = Math.max(0, modeNames.indexOf(activeTab));
      // Robust modulo for negative diffDays
      const idx = (((diffDays + modeIndex) % themes.length) + themes.length) % themes.length;
      theme = themes[idx];
    }

    const validThemes = ["legacy", "sky", "coldsteel", "daybreak"];
    if (!validThemes.includes(theme)) return "legacy";
    return theme;
  }, [userTheme, activeTab, diffDays]);

  const [charGuesses, setCharGuesses] = useDailyLocalStorage("kisekidle-chars", activeDateStr);
  const [quoteGuesses, setQuoteGuesses] = useDailyLocalStorage("kisekidle-quotes", activeDateStr);
  const [musicGuesses, setMusicGuesses] = useDailyLocalStorage("kisekidle-music", activeDateStr);
  const [locGuesses, setLocGuesses] = useDailyLocalStorage("kisekidle-locations", activeDateStr);
  const [triviaGuesses, setTriviaGuesses] = useDailyLocalStorage("kisekidle-trivia", activeDateStr);
  const [craftGuesses, setCraftGuesses] = useDailyLocalStorage("kisekidle-crafts", activeDateStr);
  const [silhouetteGuesses, setSilhouetteGuesses] = useDailyLocalStorage("kisekidle-silhouette", activeDateStr);

  // Pre-load all theme logos to prevent flickering in random mode
  useEffect(() => {
    const logos = [
      "/images/logo-Kisekidle-Zero.png",
      "/images/sky-logo.png",
      "/images/logo-Kisekidle-CS2.png",
      "/images/logo-Kisekidle-DB1.png"
    ];
    logos.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  /**
   * Returns the play status for a given mode based on stored guesses and the daily target.
   *
   * @param {Array} guesses - The player's guesses array.
   * @param {number} maxGuesses - Maximum allowed guesses.
   * @param {Array} dataArray - Full dataset for the mode.
   * @param {Array<number>} mapArray - Shuffled index map.
   * @param {Function} winCheckFn - Win-checking predicate (guess, target) => boolean.
   * @param {Date} activeDateObj - The date to evaluate.
   * @returns {{ count: number, status: "unplayed" | "won" | "lost" | "playing" }}
   */
  const getStatus = (guesses, maxGuesses, dataArray, mapArray, winCheckFn, activeDateObj) => {
    if (!guesses || guesses.length === 0) return { count: 0, status: "unplayed" };

    const targetItem = getDailyItem(dataArray, activeDateObj, mapArray);
    const hasWon = guesses.some(g => winCheckFn(g, targetItem));

    if (hasWon) return { count: guesses.length, status: "won" };
    if (guesses.length >= maxGuesses) return { count: guesses.length, status: "lost" };
    return { count: guesses.length, status: "playing" };
  };

  const modeStatuses = useMemo(() => ({
    characters: getStatus(charGuesses, 10, CHARACTERS, CHARACTERS_MAP, (g, t) => g.id === t.id, activeDateObj),
    quotes: getStatus(quoteGuesses, 6, QUOTES, QUOTES_MAP, (g, t) => g.name === t.character, activeDateObj),
    music: getStatus(musicGuesses, 6, MUSIC, MUSIC_MAP, (g, t) => g.id === t.id, activeDateObj),
    locations: getStatus(locGuesses, 5, LOCATIONS, LOCATIONS_MAP, (g, t) => g.id === t.id, activeDateObj),
    trivia: getStatus(triviaGuesses, 2, TRIVIA, TRIVIA_MAP, (g, t) => {
      if (!t) return false;
      const gLower = String(g).toLowerCase().trim();
      const aLower = t.answer.toLowerCase().trim();
      return gLower === aLower || (t.acceptedAnswers && t.acceptedAnswers.some(alt => alt.toLowerCase().trim() === gLower));
    }, activeDateObj),
    crafts: getStatus(craftGuesses, 6, CRAFTS, CRAFTS_MAP, (g, t) => g.character === t.character || (g.name || g.craftName) === (t.name || t.craftName), activeDateObj),
    silhouette: getStatus(silhouetteGuesses, 5, SILHOUETTES, SILHOUETTES_MAP, (g, t) => g === t.character, activeDateObj),
  }), [charGuesses, quoteGuesses, musicGuesses, locGuesses, triviaGuesses, craftGuesses, silhouetteGuesses, activeDateObj]);

  const [isPatchModalOpen, setIsPatchModalOpen] = useState(false);
  const [hasNewPatch, setHasNewPatch] = useState(false);
  const [isRoadmapModalOpen, setIsRoadmapModalOpen] = useState(false);

  const [jumpId, setJumpId] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Sync theme to body class for global styling
  useEffect(() => {
    document.body.className = `theme-${currentTheme}${scanlinesEnabled ? "" : " scanlines-off"}${staticBgEnabled ? " static-bg" : ""}`;
  }, [currentTheme, scanlinesEnabled, staticBgEnabled]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  /** Smooth-scrolls the page to the top. */
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /** Jumps the admin date offset to the day when a given item ID appears in the current mode's map. */
  const handleAdminJump = () => {
    let currentMap = [];
    if (activeTab === "characters") currentMap = CHARACTERS_MAP;
    if (activeTab === "quotes") currentMap = QUOTES_MAP;
    if (activeTab === "music") currentMap = MUSIC_MAP;
    if (activeTab === "locations") currentMap = LOCATIONS_MAP;
    if (activeTab === "crafts") currentMap = CRAFTS_MAP;
    if (activeTab === "trivia") currentMap = TRIVIA_MAP;
    if (activeTab === "silhouette") currentMap = SILHOUETTES_MAP;

    const targetIndex = currentMap.indexOf(Number(jumpId));

    if (targetIndex === -1) {
      alert(`ID ${jumpId} not found in the ${activeTab} map`);
      return;
    }

    const epochDate = new Date("2026-04-01T00:00:00Z");
    const today = new Date();
    const todayUTC = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate()
    );
    const todayDiffDays = Math.floor(
      (todayUTC - epochDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const requiredOffset = targetIndex - todayDiffDays;
    setAdminOffset(requiredOffset);
    setJumpId("");
  };

  /**
   * One-time migration that scans all localStorage game records to backfill
   * the overall games/wins/winRate count (ran once on mount, flagged via _meta).
   */
  const backfillOverallStatsFromHistory = () => {
    const statsStr = localStorage.getItem("kisekidle-stats");
    if (!statsStr) return;

    let stats;
    try {
      stats = JSON.parse(statsStr);
    } catch {
      return;
    }

    if (!stats || typeof stats !== "object") return;
    if (!stats._meta) stats._meta = {};
    if (stats._meta.overallBackfillV1) return;

    const modeDefs = [
      {
        key: "kisekidle-chars",
        maxGuesses: 10,
        targetArray: CHARACTERS,
        targetMap: CHARACTERS_MAP,
        hasWon: (guesses, target) => guesses.some((g) => String(g?.id) === String(target?.id)),
      },
      {
        key: "kisekidle-quotes",
        maxGuesses: 6,
        targetArray: QUOTES,
        targetMap: QUOTES_MAP,
        hasWon: (guesses, target) => guesses.some((g) => g?.name === target?.character),
      },
      {
        key: "kisekidle-music",
        maxGuesses: 6,
        targetArray: MUSIC,
        targetMap: MUSIC_MAP,
        hasWon: (guesses, target) => guesses.some((g) => String(g?.id) === String(target?.id)),
      },
      {
        key: "kisekidle-locations",
        maxGuesses: 5,
        targetArray: LOCATIONS,
        targetMap: LOCATIONS_MAP,
        hasWon: (guesses, target) => guesses.some((g) => String(g?.id) === String(target?.id)),
      },
      {
        key: "kisekidle-trivia",
        maxGuesses: 2,
        targetArray: TRIVIA,
        targetMap: TRIVIA_MAP,
        hasWon: (guesses, target) => {
          if (!target) return false;
          return guesses.some((guess) => {
            const gLower = String(guess).toLowerCase().trim();
            const aLower = String(target.answer || "").toLowerCase().trim();
            if (gLower === aLower) return true;
            return Array.isArray(target.acceptedAnswers)
              ? target.acceptedAnswers.some((alt) => String(alt).toLowerCase().trim() === gLower)
              : false;
          });
        },
      },
      {
        key: "kisekidle-crafts",
        maxGuesses: 6,
        targetArray: CRAFTS,
        targetMap: CRAFTS_MAP,
        hasWon: (guesses, target) =>
          guesses.some((g) => String(g?.name || g?.craftName) === String(target?.name || target?.craftName)),
      },
      {
        key: "kisekidle-silhouette",
        maxGuesses: 5,
        targetArray: SILHOUETTES,
        targetMap: SILHOUETTES_MAP,
        hasWon: (guesses, target) => guesses.some((g) => g === target?.character),
      },
    ];

    let totalGames = 0;
    let totalWins = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const lsKey = localStorage.key(i);
      if (!lsKey) continue;

      const matchingMode = modeDefs.find(
        (m) => lsKey === m.key || lsKey.startsWith(m.key + "-")
      );
      if (!matchingMode) continue;

      try {
        const raw = localStorage.getItem(lsKey);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const guesses = Array.isArray(parsed.guesses) ? parsed.guesses : [];
        if (guesses.length === 0) continue;

        const dateStr = parsed.date || (lsKey.startsWith(matchingMode.key + "-") ? lsKey.replace(matchingMode.key + "-", "") : null);
        if (!dateStr) continue;

        const parts = dateStr.split("-").map(Number);
        if (parts.length !== 3 || parts.some(Number.isNaN)) continue;
        const [year, month, day] = parts;
        const dateObj = new Date(Date.UTC(year, month - 1, day));

        const target = getDailyItem(matchingMode.targetArray, dateObj, matchingMode.targetMap);
        const won = matchingMode.hasWon(guesses, target);

        totalGames += 1;
        if (won) totalWins += 1;
      } catch {
        // skip malformed entries
      }
    }

    stats.overall = {
      ...stats.overall,
      games: totalGames,
      wins: totalWins,
      winRate: totalGames > 0 ? Math.round((totalWins / totalGames) * 100) + "%" : "0%",
    };
    stats._meta.overallBackfillV1 = true;
    localStorage.setItem("kisekidle-stats", JSON.stringify(stats));
  };

  useEffect(() => {
    backfillOverallStatsFromHistory();
  }, []);

  useEffect(() => {
    const latestPatchVersion = PATCHES[0].version;
    const lastSeenPatch = localStorage.getItem("kisekidle-last-patch");

    if (lastSeenPatch !== latestPatchVersion) {
      setHasNewPatch(true);
    }
  }, []);

  /** Opens the patch notes modal and persists the latest seen version. */
  const handleOpenPatchNotes = () => {
    setIsPatchModalOpen(true);
    setHasNewPatch(false);
    localStorage.setItem("kisekidle-last-patch", PATCHES[0].version);
  };

  /** Resets the archive date offset, returning to today's puzzles. */
  const handleReturnToToday = () => {
    setTargetDateObj(null);
    setActiveTab("characters");
  };

  /** Clears all game-mode localStorage entries and reloads the page. */
  const handleAdminReset = () => {
    const prefixes = ["kisekidle-chars", "kisekidle-quotes", "kisekidle-music", "kisekidle-locations", "kisekidle-trivia", "kisekidle-crafts", "kisekidle-silhouette"];
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key === p || key.startsWith(p + "-"))) {
        localStorage.removeItem(key);
      }
    }
    window.location.reload();
  };

  /** Maps theme IDs to their corresponding logo image paths. */
  const themeLogos = {
    legacy: "/images/logo-Kisekidle-Zero.png",
    sky: "/images/sky-logo.png",
    coldsteel: "/images/logo-Kisekidle-CS2.png",
    daybreak: "/images/logo-Kisekidle-DB1.png",
  };

  const urlParams = new URLSearchParams(window.location.search);
  const isDevMap = urlParams.get("devMap") === "1";

  if (isDevMap) {
    return <MapCoordLogger />;
  }

  return (
    <div className={`app-container theme-${currentTheme} ${staticBgEnabled ? "static-bg" : ""}`}>
      <button
        className="mobile-menu-btn"
        onClick={() => setIsMobileMenuOpen(true)}
        aria-label="Open menu"
      >
        ☰
      </button>

      {/* Global Material Overlay */}
      <div className="era-material-overlay"></div>

      <button
        className="settings-btn"
        onClick={() => setIsSettingsOpen(true)}
        title="Settings & Data"
      >
        ⚙️
      </button>

      {isMobileMenuOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 999,
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside className={`sidebar ${isMobileMenuOpen ? "open" : ""}`} style={isOldDomain ? { pointerEvents: "none" } : {}}>
        <div className="logo-container">
          <h1 className="logo-heading">
            <AnimatePresence mode="popLayout">
              <motion.img
                key={currentTheme}
                src={themeLogos[currentTheme]}
                alt="Kisekidle Logo"
                className="main-logo"
                initial={{ opacity: 0, scale: 0.9, filter: "brightness(1.2)" }}
                animate={{ opacity: 1, scale: 1, filter: "brightness(1)" }}
                exit={{ opacity: 0, scale: 1.05, filter: "brightness(0.8)" }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </AnimatePresence>
          </h1>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-button ${activeTab === "characters" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("characters");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>🎭 Character</span>
            <ModeBadge mode="characters" modeStatuses={modeStatuses} />
          </button>
          <button
            className={`nav-button ${activeTab === "quotes" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("quotes");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>💬 Quote</span>
            <ModeBadge mode="quotes" modeStatuses={modeStatuses} />
          </button>
          <button
            className={`nav-button ${activeTab === "music" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("music");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>🎵 Music</span>
            <ModeBadge mode="music" modeStatuses={modeStatuses} />
          </button>
          <button
            className={`nav-button ${activeTab === "locations" ? "active" : ""
              }`}
            onClick={() => {
              setActiveTab("locations");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>📍 Location</span>
            <ModeBadge mode="locations" modeStatuses={modeStatuses} />
          </button>

          {appVersion === "new" && (
            <>
              <div className="sidebar-divider"></div>
              <button
                className={`nav-button ${activeTab === "trivia" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("trivia");
                  setIsMobileMenuOpen(false);
                }}
              >
                <span>🧠 Trivia</span>
                <ModeBadge mode="trivia" modeStatuses={modeStatuses} />
              </button>
              <button
                className={`nav-button ${activeTab === "crafts" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("crafts");
                  setIsMobileMenuOpen(false);
                }}
              >
                <span>⚔️ Crafts</span>
                <ModeBadge mode="crafts" modeStatuses={modeStatuses} />
              </button>
              <button
                className={`nav-button ${activeTab === "silhouette" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("silhouette");
                  setIsMobileMenuOpen(false);
                }}
              >
                <span>👥 Silhouettes</span>
                <ModeBadge mode="silhouette" modeStatuses={modeStatuses} />
              </button>
            </>
          )}

          <div className="sidebar-divider"></div>

          <button
            className="nav-button"
            onClick={() => {
              handleOpenPatchNotes();
              setIsMobileMenuOpen(false);
            }}
            style={{ position: "relative" }}
          >
            <span>📝 Patch Notes</span>
            {hasNewPatch && (
              <span
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "15px",
                  width: "10px",
                  height: "10px",
                  backgroundColor: "#ff4d4d",
                  borderRadius: "50%",
                  boxShadow: "0 0 8px rgba(255, 77, 77, 0.8)",
                }}
              ></span>
            )}
          </button>

          <button
            className="nav-button"
            onClick={() => {
              setIsRoadmapModalOpen(true);
              setIsMobileMenuOpen(false);
            }}
          >
            <span>🗺️ Roadmap</span>
          </button>

          <button
            className="nav-button"
            onClick={() =>
              window.open("https://forms.gle/sFs45bnAQt8Bznxx9", "_blank")
            }
          >
            <span>🐛 Report Bug/Feedback</span>
          </button>
          <div className="sidebar-divider"></div>
          <button
            className={`nav-button ${activeTab === "results" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("results");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>🏆 Today's Results</span>
          </button>
          <button
            className={`nav-button ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("profile");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>👤 Profile</span>
          </button>
          <button
            className={`nav-button ${activeTab === "factions" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("factions");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>⚖️ Factions</span>
          </button>
          <button
            className={`nav-button ${activeTab === "collection" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("collection");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>📘 Collection</span>
          </button>

          <button
            className={`nav-button ${activeTab === "archive" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("archive");
              setIsMobileMenuOpen(false);
            }}
          >
            <span>📅 Day Replay</span>
          </button>

          <a
            href="https://ko-fi.com/P5P71Z3WW9"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-button kofi-sidebar-link"
          >
            <span>☕ Support on Ko-fi</span>
          </a>
        </nav>

        <FactionBadge />
        <CountdownTimer />
      </aside>

      {(process.env.NODE_ENV === "development" || localStorage.getItem("adminMode") === "true") && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#ff4d4d",
            color: "white",
            padding: "10px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "15px",
            fontWeight: "bold",
            zIndex: 9999,
            boxShadow: "0 -4px 10px rgba(0,0,0,0.3)",
          }}
        >
          <span>🛠️ ADMIN:</span>
          <div>
            <button
              onClick={() => setAdminOffset((prev) => prev - 1)}
              style={{ cursor: "pointer", color: "black", margin: "0 5px" }}
            >
              ⬅️ Prev
            </button>
            <span>
              Offset: {adminOffset > 0 ? `+${adminOffset}` : adminOffset}
            </span>
            <button
              onClick={() => setAdminOffset((prev) => prev + 1)}
              style={{ cursor: "pointer", color: "black", margin: "0 5px" }}
            >
              Next ➡️
            </button>
            <button
              onClick={handleAdminReset}
              style={{ cursor: "pointer", color: "black", margin: "0 5px" }}
            >
              Reset
            </button>
          </div>

          <div
            style={{
              borderLeft: "2px solid white",
              height: "20px",
              margin: "0 5px",
            }}
          ></div>

          {/* New Jump Feature */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span>Jump to ID:</span>
            <input
              type="number"
              value={jumpId}
              onChange={(e) => setJumpId(e.target.value)}
              placeholder="e.g. 255"
              style={{ width: "60px", padding: "2px", color: "black" }}
            />
            <button
              onClick={handleAdminJump}
              style={{
                padding: "2px 8px",
                cursor: "pointer",
                color: "black",
                fontWeight: "bold",
              }}
            >
              GO
            </button>
          </div>
        </div>
      )}

      <div
        className="bg-blur-layer"
        style={{ backgroundImage: `url("${dailyBg}")` }}
      ></div>
      <div
        className="bg-contain-layer"
        style={{ backgroundImage: `url("${dailyBg}")` }}
      ></div>
      <div className="bg-dark-tint"></div>

      <main className="main-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab + activeDateStr}
            variants={{
              initial: { opacity: 0, y: 20, scale: 0.98, filter: "blur(4px)" },
              animate: { 
                opacity: 1, 
                y: 0, 
                scale: 1, 
                filter: "blur(0px)",
                transition: {
                  type: "spring",
                  stiffness: 100,
                  damping: 20,
                  mass: 1,
                  staggerChildren: 0.1
                }
              },
              exit: { 
                opacity: 0, 
                y: -20, 
                scale: 1.02, 
                filter: "blur(4px)",
                transition: { duration: 0.3 }
              },
            }}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ width: "100%" }}
          >
            {isArchive && (
              <div className="archive-banner">
                ⚠️ You are playing an archived puzzle from {activeDateStr} ⚠️
                <button
                  className="archive-return-btn"
                  onClick={handleReturnToToday}
                >
                  Return to Today
                </button>
              </div>
            )}

            {window.location.hostname !== "kisekidle.app" && (
              <MigrationModal 
                isOpen={isOldDomain ? true : showMigrationBanner} 
                onClose={() => setShowMigrationBanner(false)} 
                currentTheme={currentTheme}
                permanent={isOldDomain}
                onOpenSettings={() => setIsSettingsOpen(true)}
              />
            )}

            <Poll />

            {activeTab === "characters" && (
              <CharacterMode
                key={activeDateStr}
                version={appVersion}
                targetDateObj={activeDateObj}
                targetDateStr={activeDateStr}
                isArchive={isArchive}
                setIsModalOpen={setIsModalOpen}
                guessedCharacters={charGuesses}
                setGuessedCharacters={setCharGuesses}
                currentTheme={currentTheme}
              />
            )}
            {activeTab === "quotes" && (
              <QuoteMode
                key={activeDateStr}
                version={appVersion}
                targetDateObj={activeDateObj}
                targetDateStr={activeDateStr}
                isArchive={isArchive}
                setIsModalOpen={setIsModalOpen}
                guessedCharacters={quoteGuesses}
                setGuessedCharacters={setQuoteGuesses}
              />
            )}
            {activeTab === "music" && (
              <MusicMode
                key={activeDateStr}
                version={appVersion}
                targetDateObj={activeDateObj}
                targetDateStr={activeDateStr}
                isArchive={isArchive}
                setIsModalOpen={setIsModalOpen}
                guessedTracks={musicGuesses}
                setGuessedTracks={setMusicGuesses}
              />
            )}
            {activeTab === "locations" && (
              <LocationMode
                key={activeDateStr}
                version={appVersion}
                targetDateObj={activeDateObj}
                targetDateStr={activeDateStr}
                isArchive={isArchive}
                setIsModalOpen={setIsModalOpen}
                guessedLocations={locGuesses}
                setGuessedLocations={setLocGuesses}
              />
            )}

            {activeTab === "trivia" && (
              <TriviaMode
                key={activeDateStr}
                version={appVersion}
                targetDateObj={activeDateObj}
                targetDateStr={activeDateStr}
                isArchive={isArchive}
                setIsModalOpen={setIsModalOpen}
                guessedOptions={triviaGuesses}
                setStoredGuesses={setTriviaGuesses}
              />
            )}
            {activeTab === "crafts" && (
              <CraftsMode
                key={activeDateStr}
                version={appVersion}
                targetDateObj={activeDateObj}
                targetDateStr={activeDateStr}
                isArchive={isArchive}
                setIsModalOpen={setIsModalOpen}
                guessedCrafts={craftGuesses}
                setGuessedCrafts={setCraftGuesses}
              />
            )}
            {activeTab === "silhouette" && (
              <SilhouetteMode
                key={activeDateStr}
                version={appVersion}
                targetDateObj={activeDateObj}
                targetDateStr={activeDateStr}
                isArchive={isArchive}
                setIsModalOpen={setIsModalOpen}
                storedGuesses={silhouetteGuesses}
                setStoredGuesses={setSilhouetteGuesses}
              />
            )}

            {activeTab === "results" && (
              <ResultsMode
                key={activeDateStr}
                version={appVersion}
                targetDateObj={activeDateObj}
                targetDateStr={activeDateStr}
                isArchive={isArchive}
                setIsModalOpen={setIsModalOpen}
                currentTheme={currentTheme}
              />
            )}
            {activeTab === "archive" && (
              <ArchiveMode
                currentTheme={currentTheme}
                onSelectDate={(date) => {
                  setTargetDateObj(date);
                  setActiveTab("characters");
                }}
              />
            )}
            {activeTab === "profile" && <ProfileMode version={appVersion} currentTheme={currentTheme} />}
            {activeTab === "factions" && <FactionMode currentTheme={currentTheme} onOpenLeaderboard={() => setIsLeaderboardOpen(true)} />}
            {activeTab === "collection" && <CollectionMode version={appVersion} currentTheme={currentTheme} collectionExpandDefault={collectionExpandDefault} />}
          </motion.div>
        </AnimatePresence>
      </main>
      <AnimatePresence>
        {isModalOpen && (
          <GlobalStatsModal
            targetDateStr={activeDateStr}
            onClose={() => setIsModalOpen(false)}
            currentTheme={currentTheme}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isPatchModalOpen && (
          <PatchNotesModal
            onClose={() => setIsPatchModalOpen(false)}
            currentTheme={currentTheme}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isRoadmapModalOpen && (
          <RoadmapModal
            onClose={() => setIsRoadmapModalOpen(false)}
            currentTheme={currentTheme}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isLeaderboardOpen && (
          <LeaderboardModal
            onClose={() => setIsLeaderboardOpen(false)}
            currentTheme={currentTheme}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isSettingsOpen && (
          <SettingsModal
            onClose={() => setIsSettingsOpen(false)}
            userTheme={userTheme}
            currentTheme={currentTheme}
            setCurrentTheme={setUserTheme}
            scanlinesEnabled={scanlinesEnabled}
            setScanlinesEnabled={setScanlinesEnabled}
            staticBgEnabled={staticBgEnabled}
            setStaticBgEnabled={setStaticBgEnabled}
            collectionExpandDefault={collectionExpandDefault}
            setCollectionExpandDefault={setCollectionExpandDefault}
            showMigrationBanner={showMigrationBanner}
            setShowMigrationBanner={setShowMigrationBanner}
          />
        )}
      </AnimatePresence>
      <Analytics />
      <button
        className={`back-to-top ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        title="Back to Top"
      >
        ▲
      </button>

      <AnimatePresence>
        {showGlobalBanner && isBannerVisible && (
          <Banner 
            text="Banner Text Placeholder - You can edit this in App.js"
            onClose={() => setIsBannerVisible(false)}
            onDismiss={() => {
              setShowGlobalBanner(false);
              setIsBannerVisible(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

